// Mermaid, loaded on first use so it stays out of the main chunk. Renders are
// serialised (Mermaid keeps global configuration), de-duplicated, and cached
// per theme so a re-render of the same document never waits for a diagram.

import type { MermaidConfig } from 'mermaid'

export type ColorScheme = 'light' | 'dark'

type MermaidApi = (typeof import('mermaid'))['default']

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"

// Mermaid's `base` theme, tinted with the markdown palette (markdown.css):
// flat indigo borders (no gradient), a soft shadow, and edge labels on the
// panel's own background.
const THEMES: Record<ColorScheme, MermaidConfig['themeVariables']> = {
  light: {
    darkMode: false,
    background: '#f8f9fb',
    fontFamily: FONT,
    fontSize: '14px',
    primaryColor: '#eef2ff',
    primaryBorderColor: '#6366f1',
    primaryTextColor: '#1a1a2e',
    secondaryColor: '#ecfdf5',
    secondaryBorderColor: '#10b981',
    secondaryTextColor: '#1a1a2e',
    tertiaryColor: '#f3f4f6',
    tertiaryBorderColor: '#d1d5db',
    tertiaryTextColor: '#1a1a2e',
    lineColor: '#6b7280',
    textColor: '#1a1a2e',
    noteBkgColor: '#fef9c3',
    noteBorderColor: '#eab308',
    noteTextColor: '#1a1a2e',
    edgeLabelBackground: '#f8f9fb',
    useGradient: false,
    dropShadow: 'drop-shadow(0 1px 2px rgba(17, 24, 39, 0.12))',
  },
  dark: {
    darkMode: true,
    background: '#16161f',
    fontFamily: FONT,
    fontSize: '14px',
    primaryColor: '#1e1b4b',
    primaryBorderColor: '#818cf8',
    primaryTextColor: '#e4e4ef',
    secondaryColor: '#132a1f',
    secondaryBorderColor: '#34d399',
    secondaryTextColor: '#e4e4ef',
    tertiaryColor: '#1a1a28',
    tertiaryBorderColor: '#3f3f56',
    tertiaryTextColor: '#e4e4ef',
    lineColor: '#9ca3af',
    textColor: '#e4e4ef',
    noteBkgColor: '#2a2410',
    noteBorderColor: '#ca8a04',
    noteTextColor: '#e4e4ef',
    edgeLabelBackground: '#16161f',
    useGradient: false,
    dropShadow: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45))',
  },
}

function configFor(scheme: ColorScheme): MermaidConfig {
  return {
    startOnLoad: false,
    securityLevel: 'strict',
    suppressErrorRendering: true,
    theme: 'base',
    darkMode: scheme === 'dark',
    fontFamily: FONT,
    themeVariables: THEMES[scheme],
  }
}

let loading: Promise<MermaidApi> | null = null

function loadMermaid(): Promise<MermaidApi> {
  loading ??= import('mermaid').then(
    (module) => module.default,
    (error: unknown) => {
      loading = null // let a later diagram retry, e.g. after a network hiccup
      throw error
    },
  )
  return loading
}

/** A finished render: the SVG, or why the diagram could not be drawn. */
export type DiagramResult = { svg: string } | { error: string }

const CACHE_LIMIT = 64
// Failures are kept too, so a broken diagram in a document being edited is
// not re-parsed on every preview refresh.
const cache = new Map<string, DiagramResult>()
const inFlight = new Map<string, Promise<DiagramResult>>()
let queue: Promise<unknown> = Promise.resolve()
let configured: ColorScheme | null = null
let sequence = 0

const cacheKey = (code: string, scheme: ColorScheme): string => `${scheme}\u0000${code}`

function remember(key: string, result: DiagramResult): DiagramResult {
  cache.set(key, result)
  if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value!)
  return result
}

/** Mermaid's message in one line: the error, and what the parser expected. */
function describe(error: unknown): string {
  const lines = (error instanceof Error ? error.message : String(error))
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  const first = lines[0] ?? 'unknown error'
  const expected = lines.find((line) => /^expecting\b/i.test(line))
  const text = expected ? `${first} ${expected}` : first
  return text.length > 240 ? `${text.slice(0, 239)}…` : text
}

/** The result for a diagram already rendered in this scheme, if any. */
export function cachedDiagram(code: string, scheme: ColorScheme): DiagramResult | undefined {
  return cache.get(cacheKey(code, scheme))
}

/** Renders a diagram; never rejects (a failure is a result). */
export function renderDiagram(code: string, scheme: ColorScheme): Promise<DiagramResult> {
  const key = cacheKey(code, scheme)
  const hit = cache.get(key)
  if (hit !== undefined) return Promise.resolve(hit)
  const pending = inFlight.get(key)
  if (pending) return pending

  const job = queue.then(async (): Promise<DiagramResult> => {
    let mermaid: MermaidApi
    try {
      mermaid = await loadMermaid()
    } catch (error) {
      return { error: `Mermaid could not be loaded (${describe(error)})` } // not cached: a retry may work
    }
    const id = `md-mermaid-${++sequence}`
    try {
      if (configured !== scheme) {
        mermaid.initialize(configFor(scheme))
        configured = scheme
      }
      const { svg } = await mermaid.render(id, code)
      return remember(key, { svg })
    } catch (error) {
      return remember(key, { error: describe(error) })
    } finally {
      // Mermaid measures in a scratch element; never leave one behind.
      document.getElementById(`d${id}`)?.remove()
    }
  })
  inFlight.set(key, job)
  // The queue only orders renders; it must keep going whatever one does.
  queue = job.then(
    () => inFlight.delete(key),
    () => inFlight.delete(key),
  )
  return job
}

/** Test hook: forget rendered diagrams and the loaded configuration. */
export function resetMermaidForTests(): void {
  cache.clear()
  inFlight.clear()
  configured = null
  loading = null
  queue = Promise.resolve()
}
